import { apiFetch } from "@/lib/apiClient";
import { UploadStatusError } from "@/features/patient/records/api/dataPipelineApi";

export type SyncJobStatus = "processing" | "complete" | "failed";

export type SyncResourceStatus = {
  status: "pending" | "done" | "error";
  stored?: number | null;
  rejected?: number | null;
  issues?: unknown[] | null;
  error?: string | null;
};

/**
 * A direct-API sync job's live status — the direct-API-integration sibling
 * of UploadStatusResponse (dataPipelineApi.ts). `provider` is a plain string
 * ("epic" today) so a future direct-API integration reuses this same shape
 * with no frontend change. processing_progress is a flat completed/total
 * over resource_types (every resource type is an equivalent unit of work),
 * unlike an upload's per-stage-weighted percent.
 */
export type SyncJobStatusResponse = {
  sync_job_id: string;
  provider: string;
  status: SyncJobStatus;
  resource_types: string[];
  resource_statuses: Record<string, SyncResourceStatus>;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  processing_stage: string | null;
  processing_progress: number | null;
  // Whole seconds between started_at and completed_at — null until the sync
  // reaches a terminal status. Same TEMP-TIMING-free field the upload side
  // marks as diagnostic-only; this one is a real, permanent feature here
  // since started_at/completed_at are already meaningful columns on
  // DirectApiSyncJob, not a diagnostic add-on.
  processing_seconds: number | null;
};

export type SyncJobsListResponse = {
  success: boolean;
  data: SyncJobStatusResponse[];
  count: number;
  hasMore: boolean;
};

function actingAsHeaders(actingAsProfileId?: string): Record<string, string> {
  return actingAsProfileId ? { "X-Acting-As-Profile-Id": actingAsProfileId } : {};
}

/** Polls the live status of a previously-enqueued direct-API sync job — the
 *  sync-job sibling of getUploadStatus. Throws UploadStatusError(404, ...)
 *  the same way getUploadStatus does for an unknown/not-yours id, so
 *  UploadHistoryContext's existing 404-is-terminal handling works unchanged. */
export async function getSyncJobStatus(syncJobId: string, actingAsProfileId?: string): Promise<SyncJobStatusResponse> {
  const { data, error, status } = await apiFetch<SyncJobStatusResponse>(
    `/api/v1/data-pipeline/sync-jobs/${syncJobId}/status`,
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) },
  );

  if (error) {
    throw new UploadStatusError(status, error.message || "Failed to fetch sync status");
  }

  return data as SyncJobStatusResponse;
}

/** Direct-API sync jobs are infrequent (a handful per patient at most) —
 *  one page is always enough, unlike upload history's paginated list. */
export async function listSyncJobs(actingAsProfileId?: string): Promise<SyncJobsListResponse> {
  const { data, error } = await apiFetch<SyncJobsListResponse>(
    "/api/v1/data-pipeline/sync-jobs",
    { method: "GET", portal: "patient", headers: actingAsHeaders(actingAsProfileId) },
  );

  if (error) {
    throw new Error(error.message || "Failed to fetch sync history");
  }

  // Same reasoning as dataPipelineApi.ts's listUploads: a 2xx response with
  // an unparseable body is a real failure, not "zero sync jobs" — don't
  // mask it as empty data.
  if (!data) {
    throw new Error("Received an empty or invalid response. Please try again.");
  }

  return data;
}
